
import AIEnhancementService from '@/services/aiEnhancementService';
import ProgramContextService from '@/services/programContextService';

export default {
  name: 'AIFeedbackCursor',
  data() {
    return {
      isProcessing: false,
      showNotification: false,
      cursorPosition: { x: 0, y: 0 },
      notificationMessage: '',
      notificationType: 'success',
      originalMessage: '', // Store original message for fallback
      aiEnhancementEnabled: true, // Toggle for AI enhancement
      notificationContext: null, // Store context for AI enhancement
      notificationDuration: 4000, // Duration in milliseconds
      remainingTime: 4000, // Remaining time in milliseconds
      progressInterval: null, // Interval for updating progress bar
      currentProgramContext: null // Store current program context
    };
  },
  computed: {
    cursorStyle() {
      return {
        left: `${this.cursorPosition.x}px`,
        top: `${this.cursorPosition.y}px`
      };
    },
    bubbleStyle() {
      return {
        left: `${this.cursorPosition.x + 30}px`,
        top: `${this.cursorPosition.y + 30}px`
      };
    },
    notificationClass() {
      return `notification-bubble--${this.notificationType}`;
    },
    notificationIcon() {
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        info: 'mdi-information',
        warning: 'mdi-alert'
      };
      return icons[this.notificationType] || 'mdi-information';
    },
    iconColor() {
      return 'white';
    },
    remainingTimePercentage() {
      if (this.notificationDuration <= 0) return 0;
      return Math.max(0, (this.remainingTime / this.notificationDuration) * 100);
    }
  },
  mounted() {
    // Check if AI enhancement is properly configured
    this.checkAIConfiguration();

    // Start cursor tracking and store listening
    this.trackCursor();
    this.listenToStore();

    // Initialize program context
    this.updateProgramContext();
  },

  beforeDestroy() {
    // Clean up intervals to prevent memory leaks
    this.stopProgressBar();
  },
  methods: {
    checkAIConfiguration() {
      const apiKeyConfigured = !!process.env.VUE_APP_OPENAI_API_KEY;
      if (apiKeyConfigured) {
        console.log('✅ AI Enhancement: OpenAI API key is configured and ready');
      } else {
        console.warn(
          '⚠️ AI Enhancement: OpenAI API key not configured - AI enhancement will be disabled'
        );
        this.aiEnhancementEnabled = false;
      }
    },
    trackCursor() {
      document.addEventListener('mousemove', e => {
        this.cursorPosition = { x: e.clientX, y: e.clientY };
      });
    },
    updateProgramContext() {
      // Update program context when component mounts or program changes
      if (ProgramContextService.isUserInProgram()) {
        this.currentProgramContext = ProgramContextService.getCurrentProgramContext();
        console.log('🔍 AIFeedbackCursor: Program context updated:', {
          programName: this.currentProgramContext.programName,
          programDesc: this.currentProgramContext.programDesc
        });
      }
    },
    listenToStore() {
      // Listen to store changes for notifications
      const store = this.$store;

      console.log('🔍 AIFeedbackCursor: Setting up store listener');

      // Watch for snackbar state changes
      this.$watch(
        () => store.state.tools.snackBar,
        newSnackbar => {
          console.log('🔍 AIFeedbackCursor: Store changed:', newSnackbar);
          if (newSnackbar.isShowSnackbar && newSnackbar.message) {
            console.log('🔍 AIFeedbackCursor: Showing notification:', newSnackbar.message);
            this.displayNotification(newSnackbar.message, newSnackbar.type, newSnackbar.context);
            // Clear the store state
            store.dispatch('tools/showSnackbar', {
              isShowSnackbar: false,
              type: '',
              message: '',
              context: null
            });
          }
        },
        { deep: true, immediate: true }
      );

      // Watch for program changes to update context
      this.$watch(
        () => store.state.user.programDoc,
        () => {
          this.updateProgramContext();
        },
        { deep: true }
      );
    },
    async displayNotification(message, type = 'success', context = null) {
      this.originalMessage = message;
      this.notificationType = type;
      this.notificationContext = context;

      // If context exists and AI enhancement is enabled, enhance the message
      if (context && this.aiEnhancementEnabled) {
        this.isProcessing = true;
        this.showNotification = false; // Hide any existing notification

        // Wait for AI enhancement
        await this.enhanceMessageWithAI(message, type, context);

        // Show enhanced message
        this.showNotification = true;
      } else {
        // Show original message immediately (no context or AI disabled)
        this.notificationMessage = message;
        this.showNotification = true;
      }

      // Calculate dynamic duration based on message length
      const duration = this.calculateNotificationDuration(this.notificationMessage);
      this.notificationDuration = duration;
      this.remainingTime = duration;

      // Start progress bar animation
      this.startProgressBar();

      // Hide after calculated duration
      setTimeout(() => {
        this.showNotification = false;
        this.stopProgressBar();
      }, duration);
    },

    async enhanceMessageWithAI(originalMessage, type, context = null) {
      try {
        // Auto-include program context if available
        let enhancedContext = { ...context };
        if (this.currentProgramContext) {
          enhancedContext = {
            ...context,
            programContext: {
              programId: this.currentProgramContext.programId,
              programName: this.currentProgramContext.programName,
              programDesc: this.currentProgramContext.programDesc,
              pathways: this.currentProgramContext.pathways,
              industries: this.currentProgramContext.industries
            }
          };

          console.log('🔍 AIFeedbackCursor: Enhanced context with program info:', {
            programName: this.currentProgramContext.programName,
            programDesc: this.currentProgramContext.programDesc
          });
        }

        // Enhance the message with AI
        const result = await AIEnhancementService.enhanceNotification({
          message: originalMessage,
          type,
          context: enhancedContext
        });

        if (result.success) {
          // Update the notification with enhanced message
          this.notificationMessage = result.enhancedMessage;
          console.log('🔍 AIFeedbackCursor: Enhanced message:', result.enhancedMessage);
        } else {
          // Fallback to original message
          this.notificationMessage = originalMessage;
          console.warn('🔍 AIFeedbackCursor: AI enhancement failed, using original message');
        }
      } catch (error) {
        console.error('🔍 AIFeedbackCursor: AI enhancement error:', error);
        // Fallback to original message on error
        this.notificationMessage = originalMessage;
      } finally {
        // Hide processing state
        this.isProcessing = false;
      }
    },

    calculateNotificationDuration(message) {
      if (!message) return 3000; // Default 3 seconds for empty messages

      // Count characters (excluding HTML tags if any)
      const textLength = message.replace(/<[^>]*>/g, '').length;

      // Base duration: 3 seconds minimum
      const baseDuration = 3000;

      // Additional time per character: 50ms per character
      const additionalTime = textLength * 50;

      // Maximum duration: 12 seconds
      const maxDuration = 12000;

      // Calculate final duration
      const duration = Math.min(baseDuration + additionalTime, maxDuration);

      console.log(
        `🔍 AIFeedbackCursor: Message length: ${textLength} chars, Duration: ${duration}ms`
      );

      return duration;
    },

    startProgressBar() {
      // Clear any existing interval
      this.stopProgressBar();

      // Update progress every 50ms for smooth animation
      this.progressInterval = setInterval(() => {
        this.remainingTime = Math.max(0, this.remainingTime - 50);
      }, 50);
    },

    stopProgressBar() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },

    simulateAIProcessing() {
      // Simulate AI processing for 3 seconds
      this.isProcessing = true;

      setTimeout(() => {
        this.isProcessing = false;
        this.displayNotification('AI feedback generated successfully!', 'success');
      }, 3000);
    }
  }
};
